import { decodeJwt } from 'jose'

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || ''
const GSI_SRC = 'https://accounts.google.com/gsi/client'
const STORAGE_KEY = '@AMB:googleIdToken'

/**
 * Browser Google sign-in, the web counterpart of the app's google-service.
 *
 * Google Identity Services returns a Google OIDC id_token whose audience is the **Web client id**
 * — the same one the app uses — and the gateway validates that id_token against Google's JWKS
 * (iss + aud), matching users by email. So, exactly as on mobile, signing in means handing that
 * id_token to `authentication.getTokens()` and letting the usual session check do the rest.
 *
 * Unlike the native SDK there is no silent refresh here: an id_token lasts ~1h and the browser
 * cannot renew it without user interaction, so it is kept for the tab (sessionStorage) and dropped
 * once expired, which sends the session check back to the login screen.
 */
class GoogleService {

  private cachedIdToken: string | null = null
  private scriptPromise: Promise<void> | null = null

  public get isConfigured(): boolean {
    return !!GOOGLE_CLIENT_ID
  }

  public get clientId(): string {
    return GOOGLE_CLIENT_ID
  }

  /** Loads the Google Identity Services script once. */
  public loadScript(): Promise<void> {
    if (!this.scriptPromise) {
      this.scriptPromise = new Promise<void>((resolve, reject) => {
        if ((window as any).google?.accounts?.id) return resolve()

        const script = document.createElement('script')
        script.src = GSI_SRC
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('Could not load Google Identity Services'))
        document.head.appendChild(script)
      })
    }
    return this.scriptPromise
  }

  /** Loads Google Identity Services and points it at the callback that receives the id_token. */
  public async setup(onCredential: (idToken: string) => void): Promise<void> {
    if (!this.isConfigured) {
      throw new Error('Google web client id is missing — regenerate .env for the NEMI build')
    }
    await this.loadScript()

    this.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response: any) => {
        this.cache(response?.credential)
        if (response?.credential) onCredential(response.credential)
      }
    })
  }

  /**
   * Renders Google's own button into the given element. Google only issues a credential from its
   * own button (or from the prompt below), so the screen keeps it as the click target underneath
   * the button it paints itself.
   */
  public renderButton(container: HTMLElement, width?: number): void {
    this.google.accounts.id.renderButton(container, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      shape: 'pill',
      text: 'continue_with',
      logo_alignment: 'center',
      width
    })
  }

  /** The other way in: Google's own prompt, for when its button could not be rendered. */
  public async prompt(): Promise<void> {
    await this.loadScript()
    this.google.accounts.id.prompt()
  }

  private get google(): any {
    const google = (window as any).google
    if (!google?.accounts?.id) throw new Error('Google Identity Services is not available')
    return google
  }

  /**
   * The id_token while it is valid, or null when there is no Google session — the token source for
   * the gateway (see authentication.getTokens).
   */
  public getIdToken(): string | null {
    const token = this.cachedIdToken ?? this.read()
    if (!token) return null

    if (this.isExpired(token)) {
      this.clear()
      return null
    }

    this.cachedIdToken = token
    return token
  }

  public clear(): void {
    this.cachedIdToken = null
    try {
      sessionStorage.removeItem(STORAGE_KEY)
      const google = (window as any).google
      google?.accounts?.id?.disableAutoSelect()
    } catch (error) {
      // ignore — nothing to clear
    }
  }

  private cache(idToken?: string | null): void {
    if (!idToken) return
    this.cachedIdToken = idToken
    try {
      sessionStorage.setItem(STORAGE_KEY, idToken)
    } catch (error) {
      // ignore — the in-memory copy still serves this page
    }
  }

  private read(): string | null {
    try {
      return sessionStorage.getItem(STORAGE_KEY)
    } catch (error) {
      return null
    }
  }

  private isExpired(idToken: string): boolean {
    try {
      const { exp } = decodeJwt(idToken)
      return !exp || exp * 1000 <= Date.now()
    } catch (error) {
      return true
    }
  }
}

export default new GoogleService()
